import * as React from 'react'

import {
  ArrowDownAZ,
  ArrowUpDown,
  ArrowUpZA,
  ChevronRight,
  Shapes,
  Table2,
} from 'lucide-react'

import {
  GroupByPanel,
  SortByPanel,
} from '~/client/src/shared/components/OptionsList'
import { renderOptionGlyph } from '~/client/src/shared/components/OptionsList/optionGlyphs'
import OptionsHeader from '~/client/src/shared/components/OptionsList/pieces/OptionsHeader'
import OptionsItem from '~/client/src/shared/components/OptionsList/pieces/OptionsItem'
import OptionsPanel from '~/client/src/shared/components/OptionsList/pieces/OptionsPanel'
import type { IOptionItem } from '~/client/src/shared/components/OptionsList/types'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import ColumnConfigPanel from './ColumnConfigPanel'
import {
  IViewOptionsColumnsProps,
  IViewOptionsGroupingProps,
  IViewOptionsPanelProps,
  IViewOptionsSortingProps,
  IViewOptionsSortValue,
} from './types'

type ViewOptionsScreen = 'main' | 'grouping' | 'sorting' | 'columns'

interface IMainRowProps {
  icon: React.ReactNode
  label: string
  value: React.ReactNode
  onClick: () => void
}

function MainRow({ icon, label, value, onClick }: IMainRowProps) {
  return (
    <OptionsItem
      role="button"
      icon={icon}
      label={label}
      onClick={onClick}
      trailing={
        <>
          <span className="tw:flex tw:min-w-0 tw:max-w-40 tw:items-center tw:gap-1.5 tw:text-app-dense-medium tw:opacity-60 tw:[&_svg]:size-4 tw:[&_svg]:shrink-0">
            {value}
          </span>
          <ChevronRight
            aria-hidden="true"
            className="tw:size-4 tw:shrink-0 tw:text-muted-foreground"
          />
        </>
      }
    />
  )
}

function getSelectedLabel(
  options: IOptionItem[],
  value: string | null,
): string {
  if (!value) return Localization.translator.none
  return options.find(option => option.key === value)?.label ?? value
}

// Value icons only when the options carry them, as in the sub-panels: a lone
// None circle beside icon-less labels would read as a stray glyph.
function OptionValue({
  options,
  value,
  leading,
  trailing,
}: {
  options: IOptionItem[]
  value: string | null
  leading?: React.ReactNode
  trailing?: React.ReactNode
}) {
  const hasIcons = options.some(option => !!option.glyph)
  const icon = value
    ? renderOptionGlyph(options.find(option => option.key === value)?.glyph)
    : hasIcons && renderOptionGlyph('none')

  return (
    <>
      {leading}
      {icon}
      <span className="tw:truncate">{getSelectedLabel(options, value)}</span>
      {trailing}
    </>
  )
}

// Read-only: direction changes on the Sort by screen. No direction without a
// sort field, and DEFAULT has none to show.
function getSortDirection(value: IViewOptionsSortValue): SortOrder | null {
  if (value.key == null) return null
  return value.order === SortOrder.ASC || value.order === SortOrder.DESC
    ? value.order
    : null
}

function SortValue({ sorting }: { sorting: IViewOptionsSortingProps }) {
  const direction = getSortDirection(sorting.value)
  const isAscending = direction === SortOrder.ASC
  const Icon = isAscending ? ArrowDownAZ : ArrowUpZA

  return (
    <OptionValue
      options={sorting.options}
      value={sorting.value.key}
      leading={direction !== null && <Icon aria-hidden="true" />}
      trailing={
        direction !== null && (
          <span className="tw:sr-only">
            {`, ${
              isAscending
                ? Localization.translator.ascending
                : Localization.translator.descending
            }`}
          </span>
        )
      }
    />
  )
}

function MainScreen({
  grouping,
  sorting,
  columns,
  onNavigate,
  onClose,
}: IViewOptionsPanelProps & {
  onNavigate: (screen: ViewOptionsScreen) => void
  onClose?: () => void
}) {
  return (
    <OptionsPanel>
      <OptionsHeader
        title={`${Localization.translator.view_noun} ${Localization.translator.options}`}
        onClose={onClose}
      />
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        {grouping && (
          <MainRow
            icon={<Shapes />}
            label={grouping.label ?? Localization.translator.groupBy}
            value={
              <OptionValue options={grouping.options} value={grouping.value} />
            }
            onClick={() => onNavigate('grouping')}
          />
        )}
        {sorting && (
          <MainRow
            icon={<ArrowUpDown />}
            label={sorting.label ?? Localization.translator.sortBy}
            value={<SortValue sorting={sorting} />}
            onClick={() => onNavigate('sorting')}
          />
        )}
        {columns && (
          <MainRow
            icon={<Table2 />}
            label={columns.label ?? Localization.translator.table}
            value={Localization.translator.xColumns(
              columns.items.filter(item => !item.isHidden).length,
            )}
            onClick={() => onNavigate('columns')}
          />
        )}
      </div>
    </OptionsPanel>
  )
}

function GroupingScreen({
  grouping,
  onBack,
  onClose,
  autoFocus,
}: {
  grouping: IViewOptionsGroupingProps
  onBack: () => void
  onClose?: () => void
  autoFocus?: boolean
}) {
  const isDirty =
    grouping.defaultValue !== undefined &&
    grouping.value !== grouping.defaultValue
  const onReset = isDirty
    ? () => {
        grouping.onChange(grouping.defaultValue ?? null)
        onBack()
      }
    : undefined

  return (
    <GroupByPanel
      title={grouping.label ?? Localization.translator.groupBy}
      options={grouping.options}
      selectedKey={grouping.value}
      onChange={grouping.onChange}
      onBack={onBack}
      onClose={onClose}
      allowNone={grouping.allowNone !== false}
      onReset={onReset}
      autoFocus={autoFocus}
    />
  )
}

function SortingScreen({
  sorting,
  onBack,
  onClose,
  autoFocus,
}: {
  sorting: IViewOptionsSortingProps
  onBack: () => void
  onClose?: () => void
  autoFocus?: boolean
}) {
  const { defaultValue } = sorting
  // A keyless sort has no meaningful direction, so order only counts toward
  // dirtiness when the default actually names a field.
  const isDirty =
    defaultValue !== undefined &&
    (sorting.value.key !== defaultValue.key ||
      (defaultValue.key != null &&
        defaultValue.order !== undefined &&
        sorting.value.order !== defaultValue.order))
  const onReset = isDirty
    ? () => {
        sorting.onChange(
          defaultValue.key
            ? {
                key: defaultValue.key,
                order: defaultValue.order ?? SortOrder.ASC,
              }
            : { key: null },
        )
        onBack()
      }
    : undefined

  return (
    <SortByPanel
      title={sorting.label ?? Localization.translator.sortBy}
      options={sorting.options}
      selectedKey={sorting.value.key}
      // Emit invariant: a picked field always carries a real direction
      // (an unset/DEFAULT order becomes ASC) and a cleared field carries none,
      // so consumers can persist the value without re-normalizing it.
      onChange={key =>
        sorting.onChange(
          key
            ? {
                key,
                order:
                  sorting.value.order === undefined ||
                  sorting.value.order === SortOrder.DEFAULT
                    ? SortOrder.ASC
                    : sorting.value.order,
              }
            : { key: null },
        )
      }
      onBack={onBack}
      onClose={onClose}
      sortOrder={sorting.value.order}
      onSortOrderChange={
        sorting.value.order !== undefined
          ? order => sorting.onChange({ ...sorting.value, order })
          : undefined
      }
      allowNone={sorting.allowNone !== false}
      onReset={onReset}
      autoFocus={autoFocus}
    />
  )
}

function ColumnsScreen({
  columns,
  onBack,
  onClose,
}: {
  columns: IViewOptionsColumnsProps
  onBack: () => void
  onClose?: () => void
}) {
  return (
    <ColumnConfigPanel
      title={columns.label ?? Localization.translator.columns}
      items={columns.items}
      onChange={columns.onChange}
      onReset={columns.onReset}
      onBack={onBack}
      onClose={onClose}
    />
  )
}

// onClose stays off IViewOptionsPanelProps: the draft hook round-trips that shape.
const ViewOptionsPanel: React.FC<
  IViewOptionsPanelProps & { onClose?: () => void }
> = props => {
  const [screen, setScreen] = React.useState<ViewOptionsScreen>('main')
  const backToMain = React.useCallback(() => setScreen('main'), [])

  if (screen === 'grouping' && props.grouping) {
    return (
      <GroupingScreen
        grouping={props.grouping}
        onBack={backToMain}
        onClose={props.onClose}
        autoFocus={props.autoFocusSearch}
      />
    )
  }

  if (screen === 'sorting' && props.sorting) {
    return (
      <SortingScreen
        sorting={props.sorting}
        onBack={backToMain}
        onClose={props.onClose}
        autoFocus={props.autoFocusSearch}
      />
    )
  }

  if (screen === 'columns' && props.columns) {
    return (
      <ColumnsScreen
        columns={props.columns}
        onBack={backToMain}
        onClose={props.onClose}
      />
    )
  }

  return <MainScreen {...props} onNavigate={setScreen} />
}

export default ViewOptionsPanel
